import React, { useContext, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Box,
  Card,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { SectionHeader } from '../SectionKit'
import { VehicleContext } from '../../SingleVehicle'
import { TenantSettingsContext } from '../../../../../App'

const sumCost = (records = []) =>
  records.reduce((total, record) => total + (Number(record.cost) || 0), 0)

// Rough cost-per-mile: no dedicated odometer log exists, so this uses the
// spread between the lowest and highest mileage reading recorded across
// services/MOTs in the selected period as a proxy — an estimate, not exact.
const estimateCostPerMile = (services, mots, grandTotal) => {
  const readings = [...services, ...mots]
    .map((record) => Number(record.mileage))
    .filter((value) => Number.isFinite(value) && value > 0)
  if (readings.length < 2) return null
  const range = Math.max(...readings) - Math.min(...readings)
  if (range <= 0) return null
  return { costPerMile: grandTotal / range, range }
}

const FinanceStat = ({ label, value, emphasis }) => (
  <Box>
    <Typography
      component="div"
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontWeight: emphasis ? 800 : 600, fontSize: '1rem' }}
      noWrap
    >
      {value}
    </Typography>
  </Box>
)

const VehicleFinances = () => {
  const { vehicle } = useContext(VehicleContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [period, setPeriod] = useState('all')
  const currency = orgSettings?.currency ?? '£'

  const cutoff = period === '12m' ? moment().subtract(12, 'months') : null

  const services = (vehicle?.services || []).filter(
    (record) => !cutoff || moment(record.date).isSameOrAfter(cutoff),
  )
  const mots = (vehicle?.mots || []).filter(
    (record) => !cutoff || moment(record.date).isSameOrAfter(cutoff),
  )
  const insurance = (vehicle?.insurance || []).filter(
    (record) => !cutoff || moment(record.dateFrom).isSameOrAfter(cutoff),
  )

  const totalServices = sumCost(services)
  const totalMots = sumCost(mots)
  const totalInsurance = sumCost(insurance)
  const grandTotal = totalServices + totalMots + totalInsurance

  const mileageEstimate = useMemo(
    () => estimateCostPerMile(services, mots, grandTotal),
    [services, mots, grandTotal],
  )

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <SectionHeader
        title="Running Costs"
        action={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={period}
            onChange={(e, value) => value && setPeriod(value)}
          >
            <ToggleButton value="all">All Time</ToggleButton>
            <ToggleButton value="12m">12mo</ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr"
        gap={1.5}
        mt={1.5}
      >
        <FinanceStat
          label="Services"
          value={`${currency}${totalServices.toFixed(2)}`}
        />
        <FinanceStat label="MOTs" value={`${currency}${totalMots.toFixed(2)}`} />
        <FinanceStat
          label="Insurance"
          value={`${currency}${totalInsurance.toFixed(2)}`}
        />
        <FinanceStat
          label="Total"
          value={`${currency}${grandTotal.toFixed(2)}`}
          emphasis
        />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1.5, display: 'block' }}
      >
        {mileageEstimate
          ? `Est. cost/mile: ${currency}${mileageEstimate.costPerMile.toFixed(2)} (${mileageEstimate.range.toLocaleString()} recorded miles this period)`
          : 'Not enough recorded mileage yet to estimate a cost per mile.'}
      </Typography>
    </Card>
  )
}

export default VehicleFinances
